import { useState } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { EMAIL_REGEX } from "repoV2/utils/common/validators/regex.constants";
import { getEmailHasValidTopLevelDomain } from "repoV2/utils/common/validators/validators";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { callGetLoyaltyListApi } from "repoV2/features/CheckoutPages/utils/apis/LoyaltyAndPromoDetailsApi/LoyaltyAndPromoDetailsApi";
import { ILoyaltyCouponDetails } from "repoV2/features/CheckoutPages/utils/apis/LoyaltyAndPromoDetailsApi/ILoyaltyDiscountSectionApi.interface";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import { deafult_applied_coupon_code_details } from "../../CouponCodeFormGroup.utils";
import {
    loyalty_email,
    loyalty_error_strings,
} from "../../CouponCodeFormGroup.constants";
import useMultipleUserDetails from "../../../UserDetailsFormGroup/utils/useMultipleUserDetails";
import { getUserFormFieldName } from "../../../UserDetailsFormGroup/UserDetailsFormGroup.utils";
import { checkout_form } from "../../../../../CheckoutForm/CheckoutForm.constants";

const {
    user_details: {
        field_names: { name, email, phone },
    },
} = checkout_form;

const { required, invalid_email } = loyalty_error_strings;

export const useLoyaltyCoupons = (props: {
    onModalClose: () => void;
    onApplyCouponCodeClick: ({
        couponType,
        code,
    }: {
        couponType?: number | undefined;
        code?: string | undefined;
    }) => void;
    setIsModalOpen: (open: boolean) => void;
}) => {
    const { onModalClose, onApplyCouponCodeClick, setIsModalOpen } = props;
    const { singleUserId } = useMultipleUserDetails();
    const [loyaltyList, setLoyaltyList] = useState<ILoyaltyCouponDetails[]>([]);
    const [isCheckButtonClicked, setIsCheckButtonClicked] = useState(false);

    const { getValues, setValue, setError, clearErrors } = useFormContext();
    const { setFormData } = useSetFormData();
    const { listingUuid, disableCheckEmailTopLevelDomain } =
        useCheckoutBasicDetailsContext();

    const priceDetails =
        useSelector(
            (mainState: IRepoV2ReduxState) =>
                mainState.checkoutPages.listingDetails[listingUuid]
                    ?.priceSection?.data
        ) || {};
    const { currency = "", currency_name: currencyName = "" } = priceDetails;

    const appliedCouponCodeData =
        useGetFormData<IAppliedCouponCodeDetails>({
            key: `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
        }) || deafult_applied_coupon_code_details;

    const emailValidator = () => {
        const data = getValues();
        const loyaltyEmail: string = data[loyalty_email];

        if (checkIsStringEmpty(loyaltyEmail)) return required;
        else if (
            !checkIsStringEmpty(loyaltyEmail) &&
            (!EMAIL_REGEX.test(loyaltyEmail) ||
                (!disableCheckEmailTopLevelDomain &&
                    !getEmailHasValidTopLevelDomain(loyaltyEmail)))
        )
            return invalid_email;
        else return true;
    };

    const onLoyaltyCheckClick = async () => {
        const data = getValues();
        const loyaltyEmail = data[loyalty_email];
        const emailValidation = emailValidator();
        if (typeof emailValidation === "string") {
            setError(loyalty_email, {
                message: emailValidation,
            });
            setLoyaltyList([]);
            setIsCheckButtonClicked(false);
            return;
        }
        clearErrors(loyalty_email);
        const list = await callGetLoyaltyListApi({
            email: loyaltyEmail,
            listingUuid,
            currency: currencyName,
        });
        setLoyaltyList(list);
        setIsCheckButtonClicked(true);
    };

    const handleModalClose = () => {
        setLoyaltyList([]);
        setIsCheckButtonClicked(false);
        onModalClose();
    };

    const onApplyLoyaltyCodeClick = ({
        couponType,
        code,
    }: {
        couponType?: number | undefined;
        code?: string | undefined;
    }) => {
        onApplyCouponCodeClick({ couponType, code });
        const singleUserEmailFieldName = getUserFormFieldName(
            singleUserId,
            email
        );
        const coupon_email = getValues(loyalty_email);
        const preFilledSingleUserEmail = getValues(singleUserEmailFieldName);
        // If its a loyalty code coupon, then we clear the already filled name and phone
        // if the email new entered is different than the originally entered user email
        if (preFilledSingleUserEmail !== coupon_email) {
            setFormData(
                `${FORM_KEYS.user_details}.${singleUserId}.${email}`,
                coupon_email
            );
            setValue(singleUserEmailFieldName, coupon_email);
            setFormData(
                `${FORM_KEYS.user_details}.${singleUserId}.${name}`,
                ""
            );
            setFormData(
                `${FORM_KEYS.user_details}.${singleUserId}.${phone}`,
                ""
            );
            const singleUserNameFieldName = getUserFormFieldName(
                singleUserId,
                name
            );
            const singleUserPhoneFieldName = getUserFormFieldName(
                singleUserId,
                phone
            );
            setValue(singleUserNameFieldName, "");
            setValue(singleUserPhoneFieldName, "");
        }
        setIsModalOpen(false);
    };

    return {
        emailValidator,
        onLoyaltyCheckClick,
        handleModalClose,
        appliedCouponCodeData,
        currency,
        isCheckButtonClicked,
        loyaltyList,
        onApplyLoyaltyCodeClick,
    };
};
